import React, { useEffect } from 'react';
import { AlertTriangle, Radiation, Shield, X } from 'lucide-react';

export default function NuclearStatusReport({ countries, currentTurn, onClose }) {
  // Add ESC key handler
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Sort countries by enrichment (highest first)
  const sortedCountries = [...countries].sort((a, b) => 
    (b.resources.uranium_enrichment || 0) - (a.resources.uranium_enrichment || 0)
  );

  const getEnrichmentStatus = (enrichment) => {
    if (enrichment >= 100) return { text: 'NUCLEAR ARMED', color: 'text-red-500', bg: 'bg-red-900', level: 'critical' };
    if (enrichment >= 90) return { text: 'IMMINENT THREAT', color: 'text-orange-500', bg: 'bg-orange-900', level: 'danger' };
    if (enrichment >= 75) return { text: 'HIGH ALERT', color: 'text-yellow-500', bg: 'bg-yellow-900', level: 'warning' };
    if (enrichment >= 50) return { text: 'DEVELOPING', color: 'text-blue-400', bg: 'bg-blue-900', level: 'moderate' };
    return { text: 'LOW THREAT', color: 'text-gray-400', bg: 'bg-gray-800', level: 'safe' };
  };

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-95 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-gray-900 via-red-950 to-gray-900 border-4 border-red-600 rounded-lg max-w-3xl w-full shadow-2xl relative"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 bg-red-600 hover:bg-red-500 text-white rounded-full p-2 transition-all hover:scale-110 shadow-lg"
        >
          <X className="w-6 h-6" />
        </button>

        {/* ALL CONTENT - COMPACT VERSION */}
        <div className="p-6">
          {/* Header - COMPACT */}
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Radiation className="w-8 h-8 text-red-500 animate-pulse" />
              <h2 className="text-3xl font-bold text-red-500">
                NUCLEAR STATUS REPORT
              </h2>
              <Radiation className="w-8 h-8 text-red-500 animate-pulse" />
            </div>
            <p className="text-lg text-gray-300">
              Turn {currentTurn} - Global Nuclear Threat Assessment
            </p>
            <div className="mt-2 inline-flex items-center gap-2 bg-red-900/50 border border-red-500 rounded-lg px-3 py-1">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-300 text-xs font-bold">
                100% ENRICHMENT = NUCLEAR CAPABILITY
              </span>
            </div>
          </div>

          {/* Countries List - COMPACT */}
          <div className="space-y-2 mb-4">
            {sortedCountries.map((country) => {
              const enrichment = country.resources.uranium_enrichment || 0;
              const status = getEnrichmentStatus(enrichment);
              const nukes = country.resources.nukes || 0;
              
              return (
                <div 
                  key={country.code}
                  className={`${status.bg} border ${
                    status.level === 'critical' ? 'border-red-500 animate-pulse' :
                    status.level === 'danger' ? 'border-orange-500' :
                    status.level === 'warning' ? 'border-yellow-500' :
                    'border-gray-600'
                  } rounded-lg p-2`}
                >
                  {/* Country Header - COMPACT */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: country.color }}
                      />
                      <span className="text-lg font-bold text-white">
                        {country.name}
                      </span>
                      {country.is_player && (
                        <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                          YOU
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {nukes > 0 && (
                        <div className="flex items-center gap-1 bg-red-900 border border-red-500 rounded px-2 py-0.5">
                          <Radiation className="w-3 h-3 text-red-400" />
                          <span className="text-red-300 font-bold text-sm">{nukes} NUKE{nukes > 1 ? 'S' : ''}</span>
                        </div>
                      )}
                      <span className={`${status.color} font-bold text-base`}>
                        {enrichment}%
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar - COMPACT */}
                  <div className="relative mb-1">
                    <div className="w-full h-6 bg-gray-800 rounded-full overflow-hidden border border-gray-700">
                      <div 
                        className={`h-full transition-all duration-1000 ${
                          enrichment >= 90 ? 'bg-gradient-to-r from-red-600 to-red-500 animate-pulse' :
                          enrichment >= 75 ? 'bg-gradient-to-r from-orange-500 to-yellow-500' :
                          enrichment >= 50 ? 'bg-gradient-to-r from-blue-500 to-blue-400' :
                          'bg-gradient-to-r from-gray-500 to-gray-400'
                        }`}
                        style={{ width: `${Math.min(enrichment, 100)}%` }}
                      />
                    </div>
                    
                    {/* Milestone Markers */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-6 bg-yellow-500 opacity-30" />
                    <div className="absolute top-0 left-3/4 transform -translate-x-1/2 w-1 h-6 bg-orange-500 opacity-30" />
                    <div className="absolute top-0 left-[90%] transform -translate-x-1/2 w-1 h-6 bg-red-500 opacity-50" />
                  </div>

                  {/* Status Label - COMPACT */}
                  <div className="flex items-center justify-between">
                    <span className={`${status.color} text-xs font-bold uppercase tracking-wide`}>
                      {status.text}
                    </span>
                    <span className="text-gray-400 text-xs">
                      {100 - enrichment}% TO NUCLEAR CAPABILITY
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Warning Summary - COMPACT */}
          <div className="bg-yellow-900/30 border border-yellow-500 rounded-lg p-2 mb-3">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-300 font-bold text-xs mb-1">STRATEGIC ASSESSMENT:</p>
                <ul className="text-yellow-200 text-xs space-y-0.5">
                  <li>• Countries with 100% enrichment can launch nuclear strikes</li>
                  <li>• Monitor closely - next report in 10 turns</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Continue Button - COMPACT */}
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold py-3 px-6 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl"
          >
            ✅ CONTINUE GAME
          </button>

          <p className="text-center text-gray-400 text-xs mt-2">
            Press ESC or click outside to close • Next report: Turn {currentTurn + 10}
          </p>
        </div>
      </div>
    </div>
  );
}
